import { motion } from "framer-motion";

// Modern, recognizable stack — pure SVG, no external deps, no emojis.
const stack = [
  {
    name: "React",
    svg: (
      <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7" aria-hidden>
        <circle cx="12" cy="12" r="1.6" fill="currentColor" />
        <g stroke="currentColor" strokeWidth="1" fill="none">
          <ellipse cx="12" cy="12" rx="10" ry="3.8" />
          <ellipse cx="12" cy="12" rx="10" ry="3.8" transform="rotate(60 12 12)" />
          <ellipse cx="12" cy="12" rx="10" ry="3.8" transform="rotate(120 12 12)" />
        </g>
      </svg>
    ),
  },
  {
    name: "Next.js",
    svg: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden>
        <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1" />
        <path d="M8 7h1.6v10H8V7zm6.4 0H16v6.4l-4.6-6.4H8.8" fillOpacity="0" stroke="currentColor" strokeWidth="1.2" />
        <path d="M9.6 7h1.4l5.2 7.3V7H17v10h-1.4L10 9.5V17H8.6" />
      </svg>
    ),
  },
  {
    name: "Tailwind",
    svg: (
      <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7" aria-hidden>
        <path
          d="M6 11c1.2-3 3-4.5 5.4-4.5 3.6 0 4.05 2.7 5.85 3.15 1.2.3 2.25-.15 3.15-1.35-1.2 3-3 4.5-5.4 4.5-3.6 0-4.05-2.7-5.85-3.15-1.2-.3-2.25.15-3.15 1.35Zm-3 6c1.2-3 3-4.5 5.4-4.5 3.6 0 4.05 2.7 5.85 3.15 1.2.3 2.25-.15 3.15-1.35-1.2 3-3 4.5-5.4 4.5-3.6 0-4.05-2.7-5.85-3.15-1.2-.3-2.25.15-3.15 1.35Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    name: "TypeScript",
    svg: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden>
        <rect x="2.5" y="2.5" width="19" height="19" rx="2" fill="currentColor" />
        <text x="12" y="16.4" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="9" fontWeight="700" fill="#fff">TS</text>
      </svg>
    ),
  },
  {
    name: "FastAPI",
    svg: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden>
        <circle cx="12" cy="12" r="10" fill="currentColor" />
        <path d="M12.5 4l-5 9h3l-1 7 5-9h-3l1-7z" fill="#fff" />
      </svg>
    ),
  },
  {
    name: "Stripe",
    svg: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden>
        <rect x="2" y="4" width="20" height="16" rx="3" fill="currentColor" />
        <text x="12" y="15.4" textAnchor="middle" fontFamily="ui-sans-serif, system-ui" fontSize="8" fontWeight="800" fill="#fff" letterSpacing="-0.5">stripe</text>
      </svg>
    ),
  },
  {
    name: "MongoDB",
    svg: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden>
        <path d="M12 2c1.6 5 5 7.5 5 12 0 4-2 6-5 8-3-2-5-4-5-8 0-4.5 3.4-7 5-12z" fill="currentColor" />
        <path d="M12 4v18" stroke="#fff" strokeWidth="0.6" />
      </svg>
    ),
  },
  {
    name: "Figma",
    svg: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden>
        <g fill="currentColor">
          <circle cx="14" cy="12" r="3" />
          <path d="M8 2h4v6H8a3 3 0 1 1 0-6zm0 8h4v6H8a3 3 0 1 1 0-6zm0 8h4a3 3 0 1 1-3 3v-3zm6-16h3a3 3 0 0 1 0 6h-3V2z" />
        </g>
      </svg>
    ),
  },
  {
    name: "Vercel",
    svg: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden>
        <path d="M12 3l10 17H2L12 3z" fill="currentColor" />
      </svg>
    ),
  },
  {
    name: "Framer",
    svg: (
      <svg viewBox="0 0 24 24" className="w-7 h-7" aria-hidden>
        <path d="M5 2h14v7H12l7 7H5v-7h7L5 9V2z" fill="currentColor" />
      </svg>
    ),
  },
];

export default function TechStack() {
  // Triple the array to make seamless infinite scroll
  const row = [...stack, ...stack, ...stack];

  return (
    <section data-testid="tech-stack-section" className="py-14 md:py-20 border-y border-zinc-100 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold">
            Tech-Stack & Standards
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-zinc-900 mt-3">
            Moderne Tools. <span className="italic">Schnelle</span> Ergebnisse.
          </h2>
          <p className="mt-3 text-sm text-zinc-500 max-w-xl mx-auto">
            Mit dem Stack, der Profi-Agenturen nutzen — nur eben ohne Agentur-Preise.
          </p>
        </motion.div>

        {/* Fade edges */}
        <div className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10" />

          <div className="flex gap-3 animate-marquee will-change-transform">
            {row.map((s, i) => (
              <div
                key={`${s.name}-${i}`}
                data-testid={`tech-${s.name.toLowerCase()}-${i}`}
                className="shrink-0 inline-flex items-center gap-2.5 rounded-2xl border border-zinc-100 bg-white px-5 py-3 text-zinc-700 hover:text-zinc-900 hover:border-zinc-300 transition-colors"
                title={s.name}
              >
                <span className="text-zinc-700">{s.svg}</span>
                <span className="text-sm font-medium tracking-tight">{s.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes marquee-scroll {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-33.3333%, 0, 0); }
        }
        .animate-marquee {
          animation: marquee-scroll 38s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}
